import React, { Component } from 'react';
import { Box } from '@mui/material';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import queryString from 'query-string';
import moment from 'moment';
import EnrollButton from '../components/trial/EnrollButton';
import TrialSearch from '../components/trial/TrialSearch';
import TrialsList from '../components/trial/TrialsList';
import SubscriptionConfirmationDialog from './SubscriptionConfirmationDialog';
import TermsConfirmationDialog from '../components/trial/TermsConfirmationDialog';
import AlreadyEnrolledWarningDialog from '../components/trial/AlreadyEnrolledWarningDialog';
import ContentBox from '../components/layout/ContentBox';
import {
  fetchTrialTags,
  showNotification,
  setLoading,
} from '../redux/actions';
import apiClient from '../loopback/apiClient';
import {
  Enrollment,
  RootState,
  ThirdPartyTrial,
  Trial,
  TrialTagMapping,
  ZerpaState,
} from '../redux/types';
import { ZerpaTableStateSetters } from '../components/layout/table/ZerpaTable';
import TrialTypeTabs from '../components/trial/TrialTypeTabs';
import { TERMS_CONTAINER_NAME } from '../environment';
import { downloadContainerUrl } from '../utilities/urlHelper';
import withRouter, { RouteType } from '../hoc/withRouter';
import NewQuipReleaseNotification from '../components/layout/NewQuipNotification';

type TrialExtended = Trial & {
  thirdPartyTrial?: ThirdPartyTrial,
  trialTagMapping?: TrialTagMapping[],
};

type State = {
  trials?: TrialExtended[];
  originalTrials?: TrialExtended[];
  enrollments?: Enrollment[];
  enrollmentDialogOpen: boolean;
  termsUrl?: string;
  query?: string;
  type?: unknown;
  evaluationOrderData?: unknown;
  alreadyEnrolledDialogOpen: boolean;
  trial?: Trial;
  tags?: number[];
  selectedTab: 'institute' | 'individual';
}

class TrialsView extends Component<Props, State> {
  static isMultiblockTrial = (trial: TrialExtended) => trial.thirdPartyTrial && trial.thirdPartyTrial.provider === 'multiblock';

  static checkEnrollmentStatus = (trial: Trial) => {
    const insideRegistration = moment().isBetween(moment(trial.registrationStartDate).local(),
      moment(trial.registrationEndDate).local(),
      'day', '[]');
    if (!insideRegistration) {
      return { notInsideRegistration: !insideRegistration };
    }

    return {};
  };

  zerpaTableSetter?: ZerpaTableStateSetters;

  constructor(props: Props) {
    super(props);
    this.state = {
      trials: [],
      enrollmentDialogOpen: false,
      termsUrl: undefined,
      query: '',
      tags: [],
      alreadyEnrolledDialogOpen: false,
      trial: undefined,
      selectedTab: 'institute',
    };
  }

  async componentDidMount() {
    await this.fetchInstituteEnrollmentsToState();
    await this.fetchTrialsToState();
    const {
      fetchTrialTags: fetchTrialTagsAction, trialTags, router,
    } = this.props;
    const parsedQueryString = queryString.parse(router.location.search);
    const { trialId, select: selection } = parsedQueryString;
    if (selection === 'institute' || selection === 'individual') {
      this.setState({ selectedTab: selection });
    }
    if (trialId) {
      apiClient.getTrial(Number(trialId)).then((trial) => {
        this.enrollToTrial(trial);
      });
    }
    if (!trialTags) {
      fetchTrialTagsAction();
    }
  }

  componentDidUpdate(prevProp: Props, prevState: State) {
    const { query, tags } = this.state;
    const { query: prevQuery, tags: prevTags } = prevState;
    const setPageTo0 = query !== prevQuery
      || tags?.length !== prevTags?.length;
    if (query !== prevQuery) {
      this.fetchTrialsToState();
    }
    if (tags?.length !== prevTags?.length) {
      this.filterTrialsWithTags();
    }
    if (this.zerpaTableSetter?.setPage && setPageTo0) {
      this.zerpaTableSetter?.setPage(0);
    }
  }

  onTypeTabChange = (tabId: 'institute' | 'individual') => {
    this.setState({ selectedTab: tabId });
    if (tabId === 'institute') {
      this.fetchInstituteEnrollmentsToState();
    } else {
      this.fetchUserEnrollmentsToState();
    }
  };

  onZerpaTableStateChange = (
    _zerpaState: ZerpaState,
    stateSetters: ZerpaTableStateSetters,
  ) => {
    if (!this.zerpaTableSetter) {
      this.zerpaTableSetter = stateSetters;
    }
  };

  filterTrialsWithTags = () => {
    const { tags, originalTrials } = this.state;
    if (tags?.length === 0) {
      this.fetchTrialsToState();
      return;
    }
    const filteredTags = originalTrials?.filter((trial) => {
      const { trialTagMapping } = trial;
      if (!trialTagMapping || trialTagMapping.length === 0) {
        return false;
      }
      const tagIds = trialTagMapping.map((tag) => tag.tagId);
      return tags?.find((tag) => tagIds.includes(tag));
    });
    this.setState({ trials: filteredTags });
  };

  fetchUserEnrollmentsToState = async () => {
    const { userId } = this.props;
    if (!userId) {
      return;
    }
    const userEnrollments = await apiClient.getUsersEnrollments(userId);
    this.setState({ enrollments: userEnrollments });
  };

  fetchInstituteEnrollmentsToState = async () => {
    const { userInstitute } = this.props;
    if (!userInstitute) {
      return;
    }
    const instituteEnrollments = await apiClient.getInstituteEnrollments(userInstitute.id);
    this.setState({ enrollments: instituteEnrollments });
  };

  fetchTrialsToState = async () => {
    const { locale, setLoading: dispatchSetLoading } = this.props;
    const { query, type } = this.state;
    const filter = {
      where: {
        type,
        and: [
          {
            registrationStartDate: {
              lte: moment().utc().startOf('day').toISOString(),
            },
          },
          {
            registrationEndDate: {
              // this should be endOf('day') but all of the trials
              // set end of the day based on startOfDay
              gte: moment().utc().startOf('day').toISOString(),
            },
          },
        ],
      },
    };
    dispatchSetLoading(true);
    const trials = await apiClient.searchTrials(filter, query || '', locale);
    dispatchSetLoading(false);
    this.setState({ trials, originalTrials: trials });
  };

  onSearch = ({ searchQuery, tags }: { searchQuery: string, tags: number[] }) => {
    this.setState({
      query: searchQuery,
      tags,
    });
  };

  handleClose = () => {
    const { enrollments, selectedTab } = this.state;
    this.setState({
      enrollmentDialogOpen: false,
      alreadyEnrolledDialogOpen: false,
      termsUrl: '',
    });
    if (enrollments) {
      if (selectedTab === 'institute') {
        this.fetchInstituteEnrollmentsToState();
      } else {
        this.fetchUserEnrollmentsToState();
      }
    }
  };

  enrollToTrial = (trial: Trial) => {
    const {
      userId,
      userInstitute,
      showNotification: showNotificationAction,
      router,
    } = this.props;
    const { trial: stateTrial, selectedTab } = this.state;
    if (!userId) {
      router.navigate(`/login?redirect=trials&trialId=${trial.id}&select=${selectedTab}`);
      return;
    }
    if (!userInstitute && !trial.isDRT) {
      // no institute cannot enroll to any trial except drt
      showNotificationAction(<FormattedMessage id="DRT.noRegularEnroll" />);
      return;
    }
    if (!stateTrial) {
      this.setState({ trial });
    }
    let alreadyEnrolledDialogOpen = false;

    // this only handles normal trial
    // exit if its already enrolled
    if (this.isAlreadyEnrolled(trial) && !TrialsView.isMultiblockTrial(trial)) {
      alreadyEnrolledDialogOpen = true;
      this.setState({ alreadyEnrolledDialogOpen });
      return;
    }

    // check if term sheet available
    if (trial.terms) {
      const termsUrl = downloadContainerUrl(TERMS_CONTAINER_NAME, trial.terms);
      this.setState({ termsUrl });
    } else {
      this.setState({ enrollmentDialogOpen: true });
    }
  };

  onTermsAccepted = () => this.setState({ termsUrl: undefined, enrollmentDialogOpen: true });

  isAlreadyEnrolled(trial: Trial) {
    const { userId } = this.props;
    const { enrollments } = this.state;
    return userId && enrollments
      ? enrollments.some((e) => e.trialId === trial.id
        && (e.status === 'approved'
          || e.status === 'applied'))
      : true;
  }

  renderTrialsActions = (trial: Trial) => {
    this.setState({ trial });
    return (
      <div className="pa2 flex justify-end">
        <EnrollButton
          onClick={() => this.setState(
            () => this.enrollToTrial(trial),
          )}
          enrollmentStatus={TrialsView.checkEnrollmentStatus(trial)}
        />
      </div>
    );
  };

  render() {
    const {
      trials,
      trial,
      enrollmentDialogOpen,
      termsUrl,
      alreadyEnrolledDialogOpen,
      selectedTab,
    } = this.state;

    const { trialTags, router } = this.props;

    return (
      <ContentBox
        title={<FormattedMessage id="menu.trials" />}
        elevation={0}
      >
        <Box sx={
            {
              margin: '8px 0 0 0',
            }
          }
        >
          <NewQuipReleaseNotification />
        </Box>
        <TrialTypeTabs
          onTabChange={this.onTypeTabChange}
          selectedTab={selectedTab}
        />
        <>
          <TrialSearch
            data={this.state}
            onSearch={this.onSearch}
            hideYearFilter
            trialTags={trialTags}
          />
          {
            trials
            && (
              <TrialsList
                trials={trials}
                renderTrialActions={this.renderTrialsActions}
                hideEndedTrials
                hideVoucherTrials
                hideInternalTrials
                localize
                onZerpaTableStateChange={this.onZerpaTableStateChange}
                selectedTab={selectedTab}
              />
            )
          }
        </>
        {
          termsUrl && (
            <TermsConfirmationDialog
              isOpen={!!termsUrl}
              fileUrl={termsUrl}
              onClose={this.handleClose}
              onAccept={this.onTermsAccepted}
            />
          )
        }
        {
          enrollmentDialogOpen && (
            <SubscriptionConfirmationDialog
              trial={trial}
              open={enrollmentDialogOpen}
              onClose={this.handleClose}
              navigate={router.navigate}
            />
          )
        }
        {
          alreadyEnrolledDialogOpen
          && (
            <AlreadyEnrolledWarningDialog
              trialName={trial?.name}
              isOpen={alreadyEnrolledDialogOpen}
              onClose={this.handleClose}
              isDrt={trial?.isDRT}
            />
          )
        }
      </ContentBox>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userId: state.users.token && state.users.token.userId,
  userInstitute: state.users.token?.institute,
  email: state.users.token && state.users.token.email,
  locale: state.intl.locale,
  trialTags: state.trialTag.byId,
});

const dispatchProps = {
  fetchTrialTags,
  showNotification,
  setLoading,
};

const connector = connect(mapStateToProps, dispatchProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & RouteType;

export default injectIntl(connector(withRouter(TrialsView)));
